import React from "react";
import {
  HStack,
  IconButton,
  Button,
  ButtonGroup,
  Select,
  Text,
  Box,
  VisuallyHidden,
} from "@chakra-ui/react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from "react-icons/fi";


const buildPageRange = (current, totalPages, maxButtons = 7) => {
  if (totalPages <= maxButtons) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set([1, totalPages, current]);
  pages.add(current - 1);
  pages.add(current + 1);
  pages.add(2);
  pages.add(totalPages - 1);

  const arr = Array.from(pages).filter((p) => p >= 1 && p <= totalPages);
  arr.sort((a, b) => a - b);

  const withEllipses = [];
  for (let i = 0; i < arr.length; i++) {
    withEllipses.push(arr[i]);
    if (i < arr.length - 1 && arr[i + 1] - arr[i] > 1) {
      withEllipses.push(-1);
    }
  }
  if (withEllipses.length > maxButtons) {
    let extras = withEllipses.length - maxButtons;
    for (let i = withEllipses.length - 2; i > 1 && extras > 0; i--) {
      const v = withEllipses[i];
      if (v > 0 && v !== current && v !== 1 && v !== totalPages) {
        withEllipses.splice(i, 1);
        extras--;
      }
    }
  }

  return withEllipses;
}

const Pagination = ({
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50],
  maxPageButtons = 7,
  isLoading = false,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  const pages = buildPageRange(currentPage, totalPages, maxPageButtons);

  return (
    <HStack justify="space-between" align="center" w="100%" mt={4} flexWrap="wrap" gap={3}>
      <HStack>
        <IconButton
          aria-label="First page"
          icon={<FiChevronsLeft />}
          onClick={() => onPageChange(1)}
          isDisabled={!canPrev || isLoading}
          size="sm"
          variant="outline"
        />
        <IconButton
          aria-label="Previous page"
          icon={<FiChevronLeft />}
          onClick={() => onPageChange(currentPage - 1)}
          isDisabled={!canPrev || isLoading}
          size="sm"
          variant="outline"
        />

        <ButtonGroup size="sm" variant="outline" isAttached>
          {pages.map((p, idx) =>
            p === -1 ? (
              <Button key={`ellipsis-${idx}`} isDisabled>
                …
              </Button>
            ) : (
              <Button
                key={p}
                onClick={() => onPageChange(p)}
                isDisabled={isLoading}
                variant={p === currentPage ? "solid" : "outline"}
              >
                {p}
                {p === currentPage && <VisuallyHidden> (current page)</VisuallyHidden>}
              </Button>
            )
          )}
        </ButtonGroup>

        <IconButton
          aria-label="Next page"
          icon={<FiChevronRight />}
          onClick={() => onPageChange(currentPage + 1)}
          isDisabled={!canNext || isLoading}
          size="sm"
          variant="outline"
        />
        <IconButton
          aria-label="Last page"
          icon={<FiChevronsRight />}
          onClick={() => onPageChange(totalPages)}
          isDisabled={!canNext || isLoading}
          size="sm"
          variant="outline"
        />
      </HStack>

      <HStack>
        <Text fontSize="sm" color="gray.600">
          Page {currentPage} of {totalPages} • {totalItems} results
        </Text>

        {onPageSizeChange && (
          <HStack>
            <Text fontSize="sm" color="gray.600">Rows per page:</Text>
            <Select
              size="sm"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              w="auto"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Select>
          </HStack>
        )}
      </HStack>
    </HStack>
  );
};

export default Pagination;
